import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LayoutDashboard,
  Landmark,
  Receipt,
  FolderOpen,
  BarChart3,
  Settings,
  Menu,
  LogOut,
  User,
  ChevronRight,
  ChevronDown,
  Building2,
  PanelLeftClose,
  PanelLeft,
  ArrowLeftRight,
  Wrench,
  FileSearch,
  Upload,
  Store,
} from "lucide-react";

const mainNavItems = [
  {
    title: "My Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
    roles: ["admin", "manager", "controller", "contributor", "viewer"],
  },
  {
    title: "Transactions",
    icon: Receipt,
    path: "/transactions",
    roles: ["admin", "manager", "controller", "contributor", "viewer"],
  },
  {
    title: "Inbox",
    icon: FolderOpen,
    path: "/inbox",
    roles: ["admin", "manager", "controller", "contributor"],
  },
  {
    title: "Analytics",
    icon: BarChart3,
    path: "/analytics",
    roles: ["admin", "manager"],
    children: [
      {
        title: "Expenses & Vendors",
        icon: Receipt,
        path: "/analytics/expenses",
        roles: ["admin", "manager"],
      },
    ],
  },
  {
    title: "Loans",
    icon: Landmark,
    path: "/loans",
    roles: ["admin", "manager"],
  },
  {
    title: "Vendors",
    icon: Store,
    path: "/vendors",
    roles: ["admin", "manager"],
  },
  {
    title: "Settings",
    icon: Settings,
    path: "/settings",
    roles: ["admin", "manager"],
  },
];

const adminToolsItems = [
  {
    title: "Reconciliation",
    icon: ArrowLeftRight,
    path: "/reconciliation",
    roles: ["admin", "manager"],
  },
  {
    title: "Ecobank Letter",
    icon: Building2,
    path: "/ecobank-letter",
    roles: ["admin", "manager"],
  },
];

const NavItem = ({ item, isActive, onClick, collapsed }) => {
  const Icon = item.icon;

  const content = (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={`
        flex items-center gap-3 rounded-xl text-sm font-medium
        transition-all duration-200 group
        ${collapsed ? "px-3 py-3 justify-center" : "px-4 py-3"}
        ${
          isActive
            ? "bg-emerald-50 text-emerald-700 shadow-sm"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }
      `}
      data-testid={`nav-${item.path.replace("/", "")}`}
    >
      <Icon
        className={`w-5 h-5 transition-colors flex-shrink-0 ${isActive ? "text-emerald-600" : "text-slate-400 group-hover:text-slate-600"}`}
      />
      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.title}</span>
          {isActive && <ChevronRight className="w-4 h-4 text-emerald-400" />}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>
          {item.title}
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
};

const SubNavItem = ({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      end
      className={({ isActive }) => `
        flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all
        ${
          isActive
            ? "bg-emerald-50 text-emerald-700"
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        }
      `}
      data-testid={`nav-${item.path.replace(/^\//, "").replace(/\//g, "-")}`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 truncate">{item.title}</span>
    </NavLink>
  );
};

const Sidebar = ({ onNavigate, collapsed, onToggleCollapse }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [adminToolsOpen, setAdminToolsOpen] = useState(() => {
    // Open by default if user is on a page within admin tools
    return (
      location.pathname.startsWith("/reconciliation") ||
      location.pathname.startsWith("/ecobank-letter")
    );
  });

  const filteredMainItems = mainNavItems.filter((item) =>
    item.roles.includes(user?.role || "viewer")
  );
  
  const filteredAdminItems = adminToolsItems.filter((item) =>
    item.roles.includes(user?.role || "viewer")
  );
  
  const showAdminTools = filteredAdminItems.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`border-b border-slate-100 ${collapsed ? "px-3 py-4" : "px-4 py-4"}`}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          {collapsed ? (
            <img 
              src="/favicon.png" 
              alt="Edomatch" 
              className="w-10 h-10 object-contain"
            />
          ) : (
            <img 
              src="/logo.png" 
              alt="Edomatch Financials" 
              className="h-10 object-contain max-w-[180px]"
            />
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-4 overflow-y-auto ${collapsed ? "px-2" : "px-3"}`}>
        <TooltipProvider delayDuration={0}>
          {/* Main Nav Items */}
          <div className="space-y-1">
            {filteredMainItems.map((item) => {
              const childItems = (item.children || []).filter((c) =>
                c.roles.includes(user?.role || "viewer")
              );
              return (
                <div key={item.path} className="space-y-1">
                  <NavItem
                    item={item}
                    isActive={location.pathname.startsWith(item.path)}
                    onClick={onNavigate}
                    collapsed={collapsed}
                  />
                  {childItems.length > 0 &&
                    (collapsed ? (
                      childItems.map((child) => (
                        <NavItem
                          key={child.path}
                          item={child}
                          isActive={location.pathname.startsWith(child.path)}
                          onClick={onNavigate}
                          collapsed={collapsed}
                        />
                      ))
                    ) : (
                      <div className="ml-5 pl-3 border-l border-slate-100 space-y-1">
                        {childItems.map((child) => (
                          <SubNavItem key={child.path} item={child} onClick={onNavigate} />
                        ))}
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
          
          {/* Admin Tools Section */}
          {showAdminTools && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              {collapsed ? (
                // Collapsed: just show items with tooltips
                <div className="space-y-1">
                  {filteredAdminItems.map((item) => (
                    <NavItem
                      key={item.path}
                      item={item}
                      isActive={location.pathname.startsWith(item.path)}
                      onClick={onNavigate}
                      collapsed={collapsed}
                    />
                  ))}
                </div>
              ) : (
                // Expanded: show collapsible section
                <>
                  <button
                    onClick={() => setAdminToolsOpen(!adminToolsOpen)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                  >
                    <Wrench className="w-4 h-4" />
                    <span className="flex-1 text-left">Admin Tools</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${adminToolsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {adminToolsOpen && (
                    <div className="mt-1 space-y-1">
                      {filteredAdminItems.map((item) => (
                        <NavItem
                          key={item.path}
                          item={item}
                          isActive={location.pathname.startsWith(item.path)}
                          onClick={onNavigate}
                          collapsed={collapsed}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </TooltipProvider>
      </nav>

      {/* Collapse Button */}
      {onToggleCollapse && (
        <div className={`px-3 py-2 border-t border-slate-100`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            className={`w-full ${collapsed ? "px-0 justify-center" : "justify-start"}`}
            data-testid="toggle-sidebar"
          >
            {collapsed ? (
              <PanelLeft className="w-5 h-5 text-slate-400" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5 text-slate-400 mr-2" />
                <span className="text-slate-500 text-sm">Collapse</span>
              </>
            )}
          </Button>
        </div>
      )}

      {/* Footer */}
      {!collapsed && (
        <div className="px-3 py-3 border-t border-slate-100">
          <div className="px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50">
            <p className="text-xs font-medium text-emerald-700">Wise & ING</p>
            <p className="text-xs text-emerald-600 mt-0.5">3 Accounts Connected</p>
          </div>
        </div>
      )}
    </div>
  );
};

const SIDEBAR_COLLAPSED_KEY = "bankadmin_sidebar_collapsed";

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === "true";
  });

  const handleMobileNavigate = () => {
    setMobileOpen(false);
  };

  const toggleSidebar = () => {
    const newValue = !sidebarCollapsed;
    setSidebarCollapsed(newValue);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
  };

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "U";

  const sidebarWidth = sidebarCollapsed ? "w-16" : "w-56";
  const contentPadding = sidebarCollapsed ? "lg:pl-16" : "lg:pl-56";
  const headerLeft = sidebarCollapsed ? "left-16" : "left-56";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:bg-white lg:border-r lg:border-slate-100 transition-all duration-300 ${sidebarWidth}`}
      >
        <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="flex items-center justify-between px-4 h-14">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-56">
              <Sidebar onNavigate={handleMobileNavigate} collapsed={false} />
            </SheetContent>
          </Sheet>

          <img 
            src="/logo.png" 
            alt="Edomatch Financials" 
            className="h-8 object-contain"
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                data-testid="user-menu-btn"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 capitalize">
                  {user?.role}
                </span>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <a href="/profile" className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Desktop Header */}
      <header 
        className={`hidden lg:block fixed top-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100 transition-all duration-300 ${headerLeft}`}
      >
        <div className="flex items-center justify-end px-6 h-14">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 h-auto py-1.5 px-2 rounded-xl hover:bg-slate-100"
                data-testid="desktop-user-menu"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">
                    {user?.name}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">
                    {user?.role}
                  </p>
                </div>
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <a href="/profile" className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                data-testid="desktop-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className={`pt-14 transition-all duration-300 ${contentPadding}`}>
        <div className="p-4 lg:p-6 animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
